import { useId } from 'react'
import type { ReactNode, JSX } from 'react'

import {
  Flex,
  Heading,
  IconButton,
  InfoIcon,
  Popover,
  Skeleton,
  Text,
} from '@buffer-mono/popcorn'

import { ProgressRing } from './ProgressRing'

import styles from './StatItem.module.css'

type StatItemProps = {
  tileContent: ReactNode
  tileClassName?: string
  /** Portion of the tile's progress ring to fill, from 0 (empty) to 1 (full). */
  fill?: number
  label: string
  /**
   * Shorter stand-in for `label`, shown once the tile is too narrow to keep the
   * label row on one line. Omit to always show the full label.
   */
  shortLabel?: string
  /** Rich content shown in the popover. */
  popoverContent: ReactNode
  /**
   * Optional element shown on the label row, after the info trigger. Keeps
   * adornments such as a badge out of the value line, which has to stay on one
   * row for the labels across the tiles to align.
   */
  labelAccessory?: ReactNode
  value: ReactNode
  loading?: boolean
  'data-testid'?: string
}

export function StatItem({
  tileContent,
  tileClassName,
  fill = 0,
  label,
  shortLabel,
  popoverContent,
  labelAccessory,
  value,
  loading = false,
  'data-testid': dataTestId,
}: StatItemProps): JSX.Element {
  const labelId = useId()

  return (
    <div
      className={styles.item}
      data-testid={dataTestId}
      role={loading ? undefined : 'group'}
      aria-labelledby={loading ? undefined : labelId}
    >
      <ProgressRing
        fill={loading ? 0 : fill}
        className={tileClassName}
        data-testid={dataTestId ? `${dataTestId}-tile` : undefined}
      >
        {loading ? (
          <Skeleton
            rounded
            className={styles.tileSkeleton}
            height={20}
            width={20}
          />
        ) : (
          tileContent
        )}
      </ProgressRing>
      <div className={styles.copy}>
        {loading ? (
          <>
            <div className={styles.skeletonLabelRow}>
              <Skeleton height="0.6em" width="8em" />
            </div>
            <div className={styles.skeletonValueRow}>
              <Skeleton height="0.6em" width="10em" />
            </div>
          </>
        ) : (
          <>
            <Flex align="center" gap="2xs">
              <Heading as="p" id={labelId} size="xs">
                {shortLabel == null ? (
                  label
                ) : (
                  <>
                    <span className={styles.labelFull}>{label}</span>
                    <span className={styles.labelShort}>{shortLabel}</span>
                  </>
                )}
              </Heading>
              <Popover>
                <Popover.Trigger>
                  <IconButton
                    type="button"
                    label={`About ${label}`}
                    size="small"
                    variant="tertiary"
                  >
                    <InfoIcon color="subtle" size="xsmall" />
                  </IconButton>
                </Popover.Trigger>
                <Popover.Content
                  side="top"
                  align="center"
                  aria-label={`About ${label}`}
                  arrow
                >
                  {popoverContent}
                </Popover.Content>
              </Popover>
              {labelAccessory}
            </Flex>
            {value != null && (
              <Text
                color="subtle"
                data-testid={dataTestId ? `${dataTestId}-value` : undefined}
              >
                {value}
              </Text>
            )}
          </>
        )}
      </div>
    </div>
  )
}
